import { LitElement, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "@phosphor-icons/webcomponents/PhBroadcast";
import "../confirmation/wow-confirmation";
import "../button/wow-button";
import { rpc } from "ui-common/services/rpc";
import { NotificationService } from "../notification/notification-service";

import { styles } from "./go-live-button.css";

export interface GoLiveButtonTranslations {
  goLive: string;
  goingLive: string;
  goLiveSuccess: string;
  goLiveFailed: string;
  goLiveHeader: string;
  goLiveConfirm: string;
  goLiveButton: string;
  goLiveCancel: string;
}

const defaultTranslations: GoLiveButtonTranslations = {
  goLive: "Go Live",
  goingLive: "Going Live...",
  goLiveSuccess: "You are now live!",
  goLiveFailed: "Failed to go live. Please try again.",
  goLiveHeader: "Go Live",
  goLiveConfirm: "Are you ready to go live? Your stream will become visible to all fans who have access.",
  goLiveButton: "Yes, Go Live",
  goLiveCancel: "Cancel",
};

@customElement("go-live-button")
export class GoLiveButton extends LitElement {
  static override styles = [styles];

  @property({ type: String })
  eventId!: string;

  @property({ type: Object })
  translations: Partial<GoLiveButtonTranslations> = {};

  @property({ type: String })
  variant: "primary" | "danger" | "neutral" = "danger";

  @property({ type: String })
  size: "small" | "medium" | "large" = "large";

  @property({ type: Boolean, attribute: "full-width" })
  fullWidth = true;

  @state()
  private isGoingLive = false;

  @state()
  private showConfirmation = false;

  private get t(): GoLiveButtonTranslations {
    return { ...defaultTranslations, ...this.translations };
  }

  private handleGoLiveClick() {
    this.showConfirmation = true;
  }

  private handleCancelGoLive() {
    this.showConfirmation = false;
  }

  private async handleConfirmGoLive() {
    if (!this.eventId) return;

    try {
      this.isGoingLive = true;
      this.showConfirmation = false;
      const result = await rpc("start-live-event", { event_id: this.eventId });

      if (result.success) {
        NotificationService.success(this.t.goLiveSuccess);
        this.dispatchEvent(
          new CustomEvent("event-went-live", {
            detail: { eventId: this.eventId },
            bubbles: true,
            composed: true,
          }),
        );
      } else {
        NotificationService.error(this.t.goLiveFailed);
      }
    } catch (error) {
      console.error("Error starting live event:", error);
      NotificationService.error(this.t.goLiveFailed);
    } finally {
      this.isGoingLive = false;
    }
  }

  override render() {
    return html`
      <wow-button
        variant=${this.variant}
        size=${this.size}
        ?full-width=${this.fullWidth}
        @click=${this.handleGoLiveClick}
        ?disabled=${this.isGoingLive}
      >
        <ph-broadcast size="18" weight="fill"></ph-broadcast>
        ${this.isGoingLive ? this.t.goingLive : this.t.goLive}
      </wow-button>
      ${this.showConfirmation
        ? html`
            <wow-confirmation header="${this.t.goLiveHeader}">
              <div class="confirmation-content">
                <p>${this.t.goLiveConfirm}</p>
                <div class="confirmation-buttons">
                  <wow-button variant="danger" size="large" full-width @click=${this.handleConfirmGoLive}>
                    ${this.t.goLiveButton}
                  </wow-button>
                  <wow-button variant="neutral" size="large" full-width @click=${this.handleCancelGoLive}>
                    ${this.t.goLiveCancel}
                  </wow-button>
                </div>
              </div>
            </wow-confirmation>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "go-live-button": GoLiveButton;
  }
}
